/**
 * ПИН-код как замок приложения.
 *
 * Сам ПИН не хранится нигде — только его отпечаток, полученный PBKDF2 со
 * случайной солью. Разница не теоретическая: четырёхзначный код перебирается
 * за мгновение, и хранить его открытым текстом означало бы отдать замок
 * вместе с ключом любому, кто заглянул в базу.
 *
 * Итераций столько же, сколько у резервных копий, — 310 000. Для человека это
 * доли секунды при разблокировке, для перебора всех десяти тысяч четырёхзначных
 * комбинаций — уже часы, а не секунды.
 *
 * ## Чего этот замок не делает
 *
 * Он не шифрует данные. Записи лежат в базе как лежали, и человек, который
 * откроет её напрямую, прочитает всё, не касаясь ПИНа. Замок закрывает
 * обычный путь — «взял чужой телефон и открыл приложение», — и врать об этом
 * в настройках нельзя.
 */

/** Столько же, сколько у резервных копий: рекомендация OWASP. */
export const PIN_ITERATIONS = 310_000;

/** Короче четырёх цифр замок перестаёт быть замком. */
export const MIN_PIN_LENGTH = 4;

export interface PinDigest {
  salt: string;
  hash: string;
  iterations: number;
}

export async function hashPin(pin: string): Promise<PinDigest> {
  const salt = crypto.getRandomValues(new Uint8Array(new ArrayBuffer(16)));
  const hash = await derive(pin, salt, PIN_ITERATIONS);

  return { salt: toBase64(salt), hash: toBase64(hash), iterations: PIN_ITERATIONS };
}

/**
 * Сверяет ПИН с отпечатком.
 *
 * Число итераций берётся из самого отпечатка: замок, поставленный год назад,
 * должен открываться и после того, как рекомендация изменится.
 */
export async function verifyPin(pin: string, digest: PinDigest): Promise<boolean> {
  const hash = await derive(pin, fromBase64(digest.salt), digest.iterations || PIN_ITERATIONS);
  return equalConstantTime(toBase64(hash), digest.hash);
}

async function derive(
  pin: string,
  salt: Uint8Array<ArrayBuffer>,
  iterations: number,
): Promise<Uint8Array<ArrayBuffer>> {
  const material = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(pin),
    'PBKDF2',
    false,
    ['deriveBits'],
  );

  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt, iterations, hash: 'SHA-256' },
    material,
    256,
  );

  return new Uint8Array(bits);
}

/**
 * Сравнение за постоянное время.
 *
 * Обычное `===` выходит из цикла на первом различии, и по времени ответа
 * можно подбирать код по одной цифре. Разница ничтожна, но замок — не то
 * место, где стоит оставлять такую щель.
 */
function equalConstantTime(a: string, b: string): boolean {
  if (a.length !== b.length) return false;

  let diff = 0;
  for (let index = 0; index < a.length; index += 1) diff |= a.charCodeAt(index) ^ b.charCodeAt(index);

  return diff === 0;
}

function toBase64(bytes: Uint8Array<ArrayBuffer>): string {
  return btoa(String.fromCharCode(...bytes));
}

function fromBase64(value: string): Uint8Array<ArrayBuffer> {
  const binary = atob(value);
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));

  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}
